import { X, AlertTriangle, ExternalLink } from 'lucide-react'
import { Button } from '../ui/Button'
import type { SimilarItem } from '../../services/similarityService'
import { similarityService } from '../../services/similarityService'

interface SimilarItemsDialogProps {
 isOpen: boolean
 onClose: () => void
 onProceed: () => void
 similarItems: SimilarItem[]
 newItemTitle: string
 hierarchyType: string
}

export function SimilarItemsDialog({
 isOpen,
 onClose,
 onProceed,
 similarItems,
 newItemTitle,
 hierarchyType
}: SimilarItemsDialogProps) {
 if (!isOpen) return null

 const getTypeLabel = (type: string) => {
 const labels: Record<string, string> = {
 'GOALS_OBJECTIVES': 'Goal',
 'VISION_PURPOSE': 'Dream',
 'DREAMS': 'Dream',
 'TASKS_ACTIONS': 'Task',
 'KNOWLEDGE_SKILLS': 'Knowledge/Skill'
 }
 return labels[type] || 'Item'
 }

 const getItemUrl = (item: any, type: string) => {
 const urlMap: Record<string, string> = {
 'GOALS_OBJECTIVES': `/goals/${item.id}`,
 'VISION_PURPOSE': `/dreams/${item.id}`,
 'DREAMS': `/dreams/${item.id}`,
 'TASKS_ACTIONS': `/tasks/${item.id}`,
 'KNOWLEDGE_SKILLS': `/knowledge-skills/${item.id}`
 }
 return urlMap[type] || '#'
 }

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay">
 <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
 {/* Header */}
 <div className="flex items-center justify-between p-6 border-b border-soft">
 <div className="flex items-center gap-3">
 <AlertTriangle className="w-6 h-6 text-warn" />
 <h2 className="text-xl font-semibold text-primary">
 Similar Items Found
 </h2>
 </div>
 <button
 onClick={onClose}
 className="text-muted hover:text-secondary"
 >
 <X className="w-6 h-6" />
 </button>
 </div>

 {/* Content */}
 <div className="p-6 overflow-y-auto flex-1">
 <div className="mb-4">
 <p className="text-secondary mb-2">
 You're trying to create: <span className="font-semibold">"{newItemTitle}"</span>
 </p>
 <p className="text-secondary text-sm">
 We found {similarItems.length} similar {similarItems.length === 1 ? 'item' : 'items'} that might be the same thing:
 </p>
 </div>

 {/* Similar Items List */}
 <div className="space-y-3">
 {similarItems.map((similarItem, index) => {
 const item = similarItem.item
 const matchPercentage = Math.round(similarItem.similarity_score * 100)
 const categoryIcon = similarityService.getCategoryIcon(similarItem.similarity_category)
 const categoryLabel = similarityService.getCategoryLabel(similarItem.similarity_category)
 const categoryColor = similarityService.getCategoryColor(similarItem.similarity_category)

 return (
 <div
 key={index}
 className="border border-soft rounded-lg p-4 hover:bg-surface-alt/50 transition-colors"
 >
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <span className="text-2xl">{categoryIcon}</span>
 <span className={`text-sm font-medium ${categoryColor}`}>
 {categoryLabel} ({matchPercentage}% match)
 </span>
 </div>
 <h3 className="font-semibold text-primary mb-1">
 {item.title}
 </h3>
 {item.description && (
 <p className="text-sm text-secondary line-clamp-2">
 {item.description}
 </p>
 )}
 <div className="mt-2 flex items-center gap-4 text-xs text-muted">
 {item.status && (
 <span className="capitalize">Status: {item.status}</span>
 )}
 {item.priority && (
 <span className="capitalize">Priority: {item.priority}</span>
 )}
 </div>
 </div>
 <a
 href={getItemUrl(item, hierarchyType)}
 target="_blank"
 rel="noopener noreferrer"
 className="text-brand hover:text-brand-hover flex-shrink-0"
 title="View item"
 >
 <ExternalLink className="w-5 h-5" />
 </a>
 </div>
 </div>
 )
 })}
 </div>

 {/* Explanation */}
 <div className="mt-6 p-4 bg-brand-subtle rounded-lg">
 <p className="text-sm text-brand">
 <strong>💡 Tip:</strong> Check if one of these existing items is what you meant. 
 If not, you can still create the new {getTypeLabel(hierarchyType).toLowerCase()}.
 </p>
 </div>
 </div>

 {/* Footer */}
 <div className="flex items-center justify-end gap-3 p-6 border-t border-soft">
 <Button
 variant="outline"
 onClick={onClose}
 className="px-4 py-2"
 >
 Cancel
 </Button>
 <Button
 onClick={onProceed}
 className="px-4 py-2 bg-ok hover:bg-ok text-on-brand"
 >
 Create New Item Anyway
 </Button>
 </div>
 </div>
 </div>
 )
}

